import {Router} from '@angular/router';
import {Component, OnInit} from '@angular/core';
import {NotificationService} from '@progress/kendo-angular-notification';
import {ConfiguracoesMiaService} from '../../services/configuracoes-mia.service';
import {AutorizacaoService} from '../../services/autorizacao.service';
import {ProdutoService} from '../../services/produto.service';
import {NotificacoesService} from '../../services/notificacoes.service';
import {CATALOGO_CONFIG_MIA, OPCOES_STATUS_MIA, OpcaoCampoMia, campoDoCatalogo}
  from '../../../../server/domain/chatbot/CatalogoConfigMia';
import {AssuntoDoJeito, ControleDoAssunto, MomentoDoJeito, PARAGRAFOS_DO_JEITO, TITULO_ADMIN, VistaDoJeito,
  assuntosDoMomento, controlesDoAssunto} from '../../../../server/domain/chatbot/FrasesJeitoDeAtenderMia';

interface ParagrafoDoJeito {
  momento: MomentoDoJeito;
  titulo: string;
  assuntos: AssuntoDoJeito[];
}

/** Pedaço da frase: texto corrido ou um valor configurado, que o cartão destaca. */
export interface SegmentoDaFrase {
  texto: string;
  valor: boolean;
}

export type ModoDeLeitura = 'cartoes' | 'texto';

const CHAVE_MODO = 'jeitoDeAtender.modo';

/**
 * Jeito de atender: a configuração da Mia lida como comportamento — um assunto
 * por frase (FrasesJeitoDeAtenderMia), agrupado por momento da conversa, em
 * duas formas à escolha do usuário: cartões (ícone, título, frase com valores
 * em destaque) ou texto corrido. Uma busca filtra os assuntos pelo que o
 * catálogo sabe dos campos. Ao lado, os ajustes nos mesmos momentos, em blocos
 * colapsáveis: clicar na frase abre o bloco; mudar um valor reescreve a frase.
 * Salva pelo mesmo endpoint e payload da tela antiga (chatbot/configuracoes).
 *
 * Tudo que vai para a tela é estado recalculado em atualizeTexto(), nunca
 * getter: array novo a cada change detection recria o DOM e trava a página.
 */
@Component({
  selector: 'app-tela-jeito-de-atender-3-mia',
  templateUrl: './tela-jeito-de-atender-3-mia.component.html',
  styleUrls: ['./tela-jeito-de-atender-3-mia.component.scss'],
})
export class TelaJeitoDeAtender3MiaComponent implements OnInit {
  configuracoes: any = null;
  salvando = false;
  ehAdmin = false;
  // ativar a Mia (Modo Teste / Em Produção) é só do superadmin; desligar, qualquer um
  ehSuperadmin = false;
  statusSalvo = 'DESATIVADA';
  opcoesStatus: OpcaoCampoMia[] = OPCOES_STATUS_MIA;

  paragrafos: ParagrafoDoJeito[] = [];
  assuntosAdmin: AssuntoDoJeito[] = [];
  tituloAdmin = TITULO_ADMIN;
  vista: VistaDoJeito = null;
  assuntoAberto: AssuntoDoJeito = null;
  controlesAbertos: ControleDoAssunto[] = [];

  // forma de leitura (lembrada no navegador) e busca
  modo: ModoDeLeitura = this.modoLembrado();
  busca = '';
  /** Parágrafos depois da busca — é o que a esquerda mostra, nas duas formas. */
  paragrafosFiltrados: ParagrafoDoJeito[] = [];
  assuntosAdminFiltrados: AssuntoDoJeito[] = [];
  /** Frase de cada assunto já partida em segmentos, para os cartões destacarem os valores. */
  segmentosPorId: {[id: string]: SegmentoDaFrase[]} = {};

  // estado de edição fora do objeto salvo
  telefones: string[] = [];
  novoTelefone = '';
  editandoTelefones = false;
  toques: {[chave: string]: number[]} = {};
  // notificação "Carrinho Abandonado": formulário próprio, salva por conta própria
  notificacao: any = null;

  // upsell: itens escolhidos (na ordem) e busca no cardápio — igual à tela antiga
  produtosUpsell: any[] = [];
  opcoesBuscaUpsell: any[] = [];
  produtoUpsellSelecionado: any = null;
  private buscandoProdutoUpsell = false;
  private timerBuscaUpsell: any = null;

  constructor(private configuracoesMiaService: ConfiguracoesMiaService,
              private autorizacaoService: AutorizacaoService,
              private notificationService: NotificationService,
              private produtoService: ProdutoService,
              private notificacoesService: NotificacoesService,
              private router: Router) {}

  /** Ajuste a ajuste, com trilho e busca. */
  abraConfiguracoes() {
    this.router.navigate(['/admin/chatbot/configuracoes']);
  }

  ngOnInit(): void {
    const usuario: any = this.autorizacaoService.getUser() || {};
    this.ehAdmin = !!(usuario.admin || usuario.superadmin);
    this.ehSuperadmin = !!usuario.superadmin;
    this.carregue();
    this.notificacoesService.carregue('Carrinho Abandonado').then((notificacao: any) => {
      this.notificacao = notificacao || {ativada: true, fazerPreview: true};
    });
  }

  carregue() {
    this.configuracoesMiaService.obtenhaConfiguracoes().then((configuracoes: any) => this.receba(configuracoes));
  }

  /**
   * Coluna nova + tinyint do MySQL: valor ausente vale o padrão do catálogo (que é
   * o mesmo do domínio), e switch vira boolean. Escada ausente deriva do lembrete
   * legado da loja, não do padrão.
   */
  private receba(configuracoes: any) {
    const config = {...(configuracoes || {})};
    for (const campo of CATALOGO_CONFIG_MIA) {
      const valor = config[campo.chave];
      if (campo.tipo === 'switch') {
        config[campo.chave] = valor === undefined || valor === null ? campo.padrao : !!valor;
      } else if (campo.tipo === 'escada') {
        continue;
      } else if (valor === undefined || valor === null || (campo.tipo === 'escolha' && valor === '' && campo.padrao)) {
        config[campo.chave] = campo.padrao;
      }
    }
    this.configuracoes = config;
    this.statusSalvo = config.status || 'DESATIVADA';
    this.telefones = String(config.telefonesTeste || '').split(',').map((t) => t.trim()).filter(Boolean);
    this.toques.toquesCarrinhoMinutos = this.parseToques(config.toquesCarrinhoMinutos,
      Number(config.lembreteCarrinhoMinutos) || 10, [6, 23]);
    this.toques.toquesDescobertaMinutos = this.parseToques(config.toquesDescobertaMinutos,
      Number(config.lembreteDescobertaMinutos) || 20, [9]);
    this.atualizeTexto();
    this.carregueCardsUpsell();
  }

  /* ---------- o texto ---------- */

  /** Chamar sempre que um valor muda: reescreve as frases e recalcula controles dependentes. */
  atualizeTexto() {
    this.vista = this.monteVista();
    this.paragrafos = PARAGRAFOS_DO_JEITO
      .map((p) => ({...p, assuntos: assuntosDoMomento(p.momento, this.vista, this.ehAdmin)}))
      .filter((p) => p.assuntos.length > 0);
    this.assuntosAdmin = this.ehAdmin ? assuntosDoMomento('motor', this.vista, this.ehAdmin) : [];
    // o assunto aberto pode ter sumido (ex.: carrinho desligado esconde o upsell)
    const visiveis: AssuntoDoJeito[] = [];
    for (const p of this.paragrafos) visiveis.push(...p.assuntos);
    visiveis.push(...this.assuntosAdmin);
    if (this.assuntoAberto && visiveis.indexOf(this.assuntoAberto) < 0) this.assuntoAberto = null;
    this.controlesAbertos = this.assuntoAberto ? controlesDoAssunto(this.assuntoAberto, this.vista) : [];

    const mapa: {[id: string]: SegmentoDaFrase[]} = {};
    for (const a of visiveis) mapa[a.id] = this.parta(a);
    this.segmentosPorId = mapa;
    this.filtre();
  }

  private monteVista(): VistaDoJeito {
    return {
      ...(this.configuracoes || {}),
      toquesCarrinhoMinutos: this.toques.toquesCarrinhoMinutos || [],
      toquesDescobertaMinutos: this.toques.toquesDescobertaMinutos || [],
      upsellNomes: (this.produtosUpsell || []).map((item) => item.nome),
    };
  }

  get nomeDaMia(): string {
    return (this.configuracoes && this.configuracoes.nome) || 'Mia';
  }

  fraseDo(assunto: AssuntoDoJeito): string {
    return this.vista ? assunto.texto(this.vista) : '';
  }

  /** Algum campo do assunto saiu do padrão do catálogo. */
  assuntoAlterado(assunto: AssuntoDoJeito): boolean {
    return assunto.campos.some((chave) => {
      const campo = campoDoCatalogo(chave);
      if (!campo) return false;
      const atual = this.toques[chave] ? this.toques[chave] : this.configuracoes[chave];
      if (campo.tipo === 'escada') return JSON.stringify((atual || []).map(Number)) !== JSON.stringify(campo.padrao);
      if (campo.tipo === 'produtos') return this.produtosUpsell.length > 0;
      return (atual === undefined || atual === null ? '' : atual) !== (campo.padrao === null ? '' : campo.padrao);
    });
  }

  /** Do bloco à direita: abre/fecha. */
  abrir(assunto: AssuntoDoJeito) {
    this.assuntoAberto = this.assuntoAberto === assunto ? null : assunto;
    this.controlesAbertos = this.assuntoAberto ? controlesDoAssunto(this.assuntoAberto, this.vista) : [];
  }

  /** Da frase ou do cartão à esquerda: sempre abre o bloco correspondente e leva até ele. */
  abrirDaFrase(assunto: AssuntoDoJeito) {
    if (this.assuntoAberto !== assunto) this.abrir(assunto);
    setTimeout(() => {
      const bloco = document.getElementById('jeito-bloco-' + assunto.id);
      if (bloco) bloco.scrollIntoView({block: 'nearest', behavior: 'smooth'});
    });
  }

  abraEquipe() {
    const equipe = this.paragrafos.find((p) => p.momento === 'equipe');
    const assunto = equipe && equipe.assuntos.find((a) => a.id === 'equipe');
    if (assunto) this.abrirDaFrase(assunto);
  }

  fecheAberto() {
    this.assuntoAberto = null;
    this.controlesAbertos = [];
  }

  aoMudar() {
    this.atualizeTexto();
  }

  escolhaValor(campo: string, valor: string) {
    this.configuracoes[campo] = valor;
    this.atualizeTexto();
  }

  opcaoEscolhidaDe(controle: ControleDoAssunto): OpcaoCampoMia | undefined {
    return (controle.opcoes || []).find((opcao) => opcao.valor === this.configuracoes[controle.campo]);
  }

  /** Pílula que este usuário não pode escolher: ativar é só do superadmin (o servidor também recusa). */
  estadoBloqueado(valor: string): boolean {
    return !this.ehSuperadmin && valor !== 'DESATIVADA' && valor !== this.statusSalvo;
  }

  defineStatus(valor: string) {
    if (this.estadoBloqueado(valor)) {
      this.notifique('Só o superadmin do MeuCardápio pode ativar a Mia. Fale com a equipe para ligar o Modo Teste ou colocar Em Produção.', 'error');
      return;
    }
    this.configuracoes.status = valor;
    if (valor !== 'MODO_TESTE') this.editandoTelefones = false;
  }

  /* ---------- forma de leitura ---------- */

  mudeModo(modo: ModoDeLeitura) {
    this.modo = modo;
    try { localStorage.setItem(CHAVE_MODO, modo); } catch (e) { /* navegador sem storage: só não lembra */ }
  }

  private modoLembrado(): ModoDeLeitura {
    try { return localStorage.getItem(CHAVE_MODO) === 'texto' ? 'texto' : 'cartoes'; } catch (e) { return 'cartoes'; }
  }

  /* ---------- busca ---------- */

  /** Filtra pelo título, pela frase e pelo que o catálogo sabe dos campos (rótulo, sinônimos, por que mudar). */
  filtre() {
    const termos = this.normalize(this.busca).split(' ').filter((t) => t.length > 1);
    const casa = (a: AssuntoDoJeito) => !termos.length || termos.every((t) => this.textoDeBusca(a).indexOf(t) >= 0);
    this.paragrafosFiltrados = this.paragrafos
      .map((p) => ({momento: p.momento, titulo: p.titulo, assuntos: p.assuntos.filter(casa)}))
      .filter((p) => p.assuntos.length > 0);
    this.assuntosAdminFiltrados = this.assuntosAdmin.filter(casa);
  }

  limpeBusca() {
    this.busca = '';
    this.filtre();
  }

  get buscaSemResultado(): boolean {
    return !!this.busca.trim() && !this.paragrafosFiltrados.length && !this.assuntosAdminFiltrados.length;
  }

  private textoDeBusca(a: AssuntoDoJeito): string {
    const partes = [a.titulo, this.fraseDo(a)];
    for (const chave of a.campos) {
      const campo = campoDoCatalogo(chave);
      if (campo) partes.push(campo.rotulo, campo.porQueMudar, ...campo.sinonimos, ...(campo.opcoes || []).map((o) => o.nome));
    }
    return this.normalize(partes.join(' '));
  }

  private normalize(texto: string): string {
    return String(texto || '').normalize('NFD').replace(/[̀-ͯ]/g, '').toLowerCase()
      .replace(/[^a-z0-9]+/g, ' ').trim();
  }

  /* ---------- cartões ---------- */

  /** Quebra a frase nos trechos que são valores (destaques), na ordem em que aparecem. */
  private parta(assunto: AssuntoDoJeito): SegmentoDaFrase[] {
    const frase = this.fraseDo(assunto);
    const destaques = (assunto.destaques ? assunto.destaques(this.vista) : []).filter((d) => !!d);
    const segmentos: SegmentoDaFrase[] = [];
    let resto = frase;
    while (resto.length) {
      let primeiro = -1;
      let achado = '';
      for (const d of destaques) {
        const i = resto.indexOf(d);
        if (i >= 0 && (primeiro < 0 || i < primeiro)) { primeiro = i; achado = d; }
      }
      if (primeiro < 0) { segmentos.push({texto: resto, valor: false}); break; }
      if (primeiro > 0) segmentos.push({texto: resto.slice(0, primeiro), valor: false});
      segmentos.push({texto: achado, valor: true});
      resto = resto.slice(primeiro + achado.length);
    }
    return segmentos;
  }

  /* ---------- trackBy ---------- */

  trackPorMomento(_: number, paragrafo: ParagrafoDoJeito): string {
    return paragrafo.momento;
  }

  trackPorId(_: number, assunto: AssuntoDoJeito): string {
    return assunto.id;
  }

  trackPorCampo(_: number, controle: ControleDoAssunto): string {
    return controle.campo;
  }

  trackPorValor(_: number, opcao: OpcaoCampoMia): string {
    return opcao.valor;
  }

  trackPorIndice(indice: number): number {
    return indice;
  }

  trackPorIndiceSeg(indice: number): number {
    return indice;
  }

  /* ---------- telefones de teste ---------- */

  adicioneTelefone() {
    const telefone = (this.novoTelefone || '').replace(/[\s()\-\.]/g, '');
    if (!telefone || this.telefones.indexOf(telefone) >= 0) {
      this.novoTelefone = '';
      return;
    }
    this.telefones = [...this.telefones, telefone];
    this.novoTelefone = '';
  }

  removaTelefone(telefone: string) {
    this.telefones = this.telefones.filter((item) => item !== telefone);
  }

  /* ---------- escadas de toques (mesmas regras da tela antiga) ---------- */

  private parseToques(salvo: any, legado: number, gaps: number[]): number[] {
    let valores: any = salvo;
    if (typeof valores === 'string') {
      try { valores = JSON.parse(valores || '[]'); } catch { valores = []; }
    }
    const lista: number[] = [];
    for (const valor of (Array.isArray(valores) ? valores : [])) {
      const minutos = Number(valor);
      if (!Number.isInteger(minutos) || minutos < 1 || minutos > 60) continue;
      if (lista.length && minutos <= lista[lista.length - 1]) continue;
      lista.push(minutos);
      if (lista.length === 3) break;
    }
    if (lista.length) return lista;
    const derivada = [legado];
    for (const gap of gaps) {
      const minutos = Math.min(60, legado + gap);
      if (minutos > derivada[derivada.length - 1]) derivada.push(minutos);
    }
    return derivada;
  }

  adicioneToqueE(chave: string) {
    const toques = this.toques[chave];
    if (!toques || toques.length >= 3) return;
    const ultimo = Number(toques[toques.length - 1]) || 0;
    toques.push(Math.min(60, ultimo + (toques.length === 1 ? 6 : 17)));
    this.atualizeTexto();
  }

  removaToqueE(chave: string, indice: number) {
    const toques = this.toques[chave];
    if (!toques || toques.length <= 1) return;
    toques.splice(indice, 1);
    this.atualizeTexto();
  }

  escadaValida(chave: string): boolean {
    const toques = this.toques[chave] || [];
    if (!toques.length || toques.length > 3) return false;
    return toques.every((minutos, indice) => Number.isInteger(Number(minutos))
      && Number(minutos) >= 1 && Number(minutos) <= 60
      && (indice === 0 || Number(minutos) > Number(toques[indice - 1])));
  }

  /* ---------- upsell: itens escolhidos + busca ---------- */

  carregueCardsUpsell() {
    let ids: number[] = [];
    try {
      const salvos = JSON.parse(this.configuracoes.upsellItens || '[]');
      ids = Array.isArray(salvos) ? salvos.map(Number).filter((id) => id > 0) : [];
    } catch (e) {
      ids = [];
    }
    if (!ids.length) {
      this.produtosUpsell = [];
      this.atualizeTexto();
      return;
    }
    this.atualizeCardsUpsell(ids);
  }

  private atualizeCardsUpsell(ids: number[]) {
    this.configuracoesMiaService.obtenhaItensUpsell(ids)
      .then((itens: any) => this.produtosUpsell = itens || [])
      .catch(() => {})
      .then(() => this.atualizeTexto());
  }

  searchProdutoUpsell(texto: string) {
    if (this.timerBuscaUpsell) clearTimeout(this.timerBuscaUpsell);
    this.timerBuscaUpsell = setTimeout(() => this.busqueProdutosUpsell(texto), 500);
  }

  busquePrimeiraVezUpsell() {
    if (!this.opcoesBuscaUpsell.length) this.busqueProdutosUpsell('');
  }

  private busqueProdutosUpsell(texto: string) {
    if (this.buscandoProdutoUpsell) return;
    this.buscandoProdutoUpsell = true;
    this.produtoService.autoComplete(texto, false, true, true)
      .then((produtos: any) => this.opcoesBuscaUpsell = produtos || [])
      .catch(() => {})
      .then(() => this.buscandoProdutoUpsell = false);
  }

  adicioneProdutoUpsell(produto: any) {
    if (!produto || !produto.id) return;
    setTimeout(() => this.produtoUpsellSelecionado = null);
    if (this.produtosUpsell.length >= 3) return;
    if (this.produtosUpsell.some((item) => Number(item.id) === Number(produto.id))) return;
    this.produtosUpsell = [...this.produtosUpsell,
      {id: produto.id, nome: produto.nome, preco: produto.valor, categoria: produto.categoria, disponivel: true, elegivel: true}];
    this.atualizeCardsUpsell(this.produtosUpsell.map((item) => Number(item.id)));
  }

  removaProdutoUpsell(produto: any) {
    this.produtosUpsell = this.produtosUpsell.filter((item) => Number(item.id) !== Number(produto.id));
    this.atualizeTexto();
  }

  /* ---------- salvar ---------- */

  salve() {
    if (this.salvando || !this.configuracoes) return;
    if (!this.configuracoes.nome || String(this.configuracoes.nome).trim().length < 3) {
      this.notifique('Dê um nome com pelo menos 3 letras para a Mia.', 'error');
      return;
    }
    if (this.configuracoes.followupMiaV3
        && (!this.escadaValida('toquesCarrinhoMinutos') || !this.escadaValida('toquesDescobertaMinutos'))) {
      this.notifique('Os toques dos lembretes devem ser tempos crescentes entre 1 e 60 minutos.', 'error');
      return;
    }

    // número digitado sem clicar em "Adicionar" não pode se perder no salvamento
    this.adicioneTelefone();
    const payload = {
      ...this.configuracoes,
      telefonesTeste: this.telefones.join(','),
      toquesCarrinhoMinutos: this.toques.toquesCarrinhoMinutos.map(Number),
      toquesDescobertaMinutos: this.toques.toquesDescobertaMinutos.map(Number),
      upsellItens: JSON.stringify(this.produtosUpsell.map((item) => Number(item.id))),
    };
    this.salvando = true;
    this.configuracoesMiaService.salveConfiguracoes(payload).then((salva: any) => {
      this.receba(salva);
      this.notifique('Configurações da Mia salvas!', 'success');
    }).catch((erro: any) => {
      this.notifique(typeof erro === 'string' ? erro : 'Não consegui salvar as configurações.', 'error');
    }).then(() => this.salvando = false);
  }

  cancele() {
    this.fecheAberto();
    this.novoTelefone = '';
    this.carregue();
  }

  private notifique(mensagem: string, tipo: 'success' | 'error') {
    this.notificationService.show({
      content: mensagem,
      type: {style: tipo, icon: true},
      position: {horizontal: 'center', vertical: 'top'},
      animation: {type: 'slide', duration: 200},
      hideAfter: 3500,
    });
  }
}
